import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Clock } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  FieldError,
  Label,
  Modal,
  ModalFooter,
  Radio,
  Spinner,
  fieldControlClass,
  fieldErrorId,
} from "@/components/ui";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { cn } from "@/lib/utils";
import { activityService } from "@/services/activityService";
import { formatActivityDateTime } from "@/components/bookings/formatActivityDateTime";
import {
  ActivityAvailabilityMode,
  type SlotAvailability,
  type RegionCode,
} from "@/types";
import type { HandshakeConfirmPayload } from "./handshakeConfirmTypes";
import {
  isFutureActivityLocalVisit,
  scheduleTimeToHHmm,
  todayActivityLocalDateInput,
  visitAtIsoToDateInput,
  visitAtIsoToTimeInput,
  visitAtLocalPickerToApiIso,
} from "./visitAt";

interface HandshakeConfirmBookingModalProps {
  onClose: () => void;
  onConfirm: (payload: HandshakeConfirmPayload) => void | Promise<void>;
  requestedVisitAt?: string | null;
  requestedTimeSlot?: string | null;
  activityRegionCode: RegionCode;
  activityId: string;
  availabilityMode?: ActivityAvailabilityMode;
  partySize: number;
  isLoading: boolean;
}

type TimeChoice = "slot" | "custom";

const VISIT_DATE_ID = "handshake-confirm-visit-date";
const CUSTOM_TIME_ID = "handshake-confirm-custom-time";
const VISIT_TIME_ID = "handshake-confirm-visit-time";
const SLOTS_GROUP_ID = "handshake-confirm-slots";

type FieldErrorTarget = "visitDate" | "slot" | "time";

interface ValidationFieldError {
  message: string;
  target: FieldErrorTarget;
}

function initialVisitDate(
  requestedVisitAt: string | null | undefined,
  regionCode: RegionCode | null,
): string {
  if (requestedVisitAt) {
    return (
      visitAtIsoToDateInput(requestedVisitAt, regionCode) ??
      todayActivityLocalDateInput(regionCode)
    );
  }
  return todayActivityLocalDateInput(regionCode);
}

function requestedClockKey(
  requestedVisitAt: string | null | undefined,
  requestedTimeSlot: string | null | undefined,
  regionCode: RegionCode | null,
): string | null {
  if (requestedTimeSlot?.trim()) {
    return scheduleTimeToHHmm(requestedTimeSlot);
  }
  if (requestedVisitAt) {
    return visitAtIsoToTimeInput(requestedVisitAt, regionCode);
  }
  return null;
}

function slotTimeKey(slot: SlotAvailability): string {
  return scheduleTimeToHHmm(slot.start_time);
}

function formatSlotCapacity(
  slot: SlotAvailability,
  t: (key: string, opts?: Record<string, unknown>) => string,
  partySize: number,
): string {
  if (slot.max_capacity == null) {
    return t("handshakePages:confirmSlotUnlimited");
  }
  if (slot.is_available) {
    const remaining = slot.available_spots ?? 0;
    return t("handshakePages:confirmSlotSpotsRemaining", { count: remaining });
  }
  const needed = partySize;
  const remaining = slot.available_spots ?? 0;
  if (remaining > 0 && remaining < needed) {
    return t("handshakePages:confirmSlotInsufficient", {
      remaining,
      partySize: needed,
    });
  }
  return t("handshakePages:confirmSlotFull");
}

export function HandshakeConfirmBookingModal({
  onClose,
  onConfirm,
  requestedVisitAt,
  requestedTimeSlot,
  activityRegionCode,
  activityId,
  availabilityMode,
  partySize,
  isLoading,
}: HandshakeConfirmBookingModalProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation(["handshakePages", "common"]);
  const regionCode = activityRegionCode;
  const isScheduled = availabilityMode === ActivityAvailabilityMode.SCHEDULED;

  const requestedSlotKey = requestedClockKey(
    requestedVisitAt,
    requestedTimeSlot,
    regionCode,
  );
  const requestedTimeLabel = requestedSlotKey;

  const [visitDate, setVisitDate] = useState(() =>
    initialVisitDate(requestedVisitAt, regionCode),
  );
  const [timeChoice, setTimeChoice] = useState<TimeChoice>(() => {
    if (!isScheduled) return "custom";
    return "slot";
  });
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(() => {
    if (requestedVisitAt && isScheduled) {
      return requestedClockKey(requestedVisitAt, requestedTimeSlot, regionCode);
    }
    return null;
  });
  const [customTime, setCustomTime] = useState(() => {
    return (
      requestedClockKey(requestedVisitAt, requestedTimeSlot, regionCode) ??
      "10:00"
    );
  });
  const [fieldError, setFieldError] = useState<ValidationFieldError | null>(
    null,
  );

  const visitDateErrorId = fieldErrorId(VISIT_DATE_ID);
  const slotsErrorId = fieldErrorId(SLOTS_GROUP_ID);

  const slotsQuery = useQuery({
    queryKey: ["handshake", "confirm-slots", activityId, visitDate, partySize],
    queryFn: async () => {
      const response = await activityService.getAvailability(activityId, {
        date_from: visitDate,
        date_to: visitDate,
        people_count: partySize,
        include_all_schedule_slots: true,
      });
      return response.days.find((entry) => entry.date === visitDate) ?? null;
    },
    enabled: isScheduled && Boolean(visitDate),
  });

  const dayAvailability = slotsQuery.data ?? null;
  const slotsLoading = slotsQuery.isFetching;
  const slotsError = slotsQuery.isError;
  const slotsLoadErrorMessage = slotsError
    ? getApiErrorMessage(
        slotsQuery.error,
        "generic",
        t("handshakePages:confirmSlotsError"),
      )
    : null;
  const dayClosed = dayAvailability?.is_closed ?? false;

  const slots = useMemo(
    () => dayAvailability?.slots ?? [],
    [dayAvailability],
  );

  const canPickScheduledSlot =
    isScheduled &&
    !slotsLoading &&
    !slotsError &&
    !dayClosed &&
    slots.length > 0;

  const slotKeys = useMemo(() => slots.map(slotTimeKey), [slots]);

  const effectiveSelectedSlotKey = useMemo(() => {
    if (!canPickScheduledSlot) {
      return null;
    }
    if (selectedSlotKey && slotKeys.includes(selectedSlotKey)) {
      return selectedSlotKey;
    }
    if (
      requestedVisitAt &&
      visitDate === visitAtIsoToDateInput(requestedVisitAt, regionCode)
    ) {
      const requestedKey =
        requestedClockKey(requestedVisitAt, requestedTimeSlot, regionCode) ??
        "";
      if (requestedKey && slotKeys.includes(requestedKey)) {
        return requestedKey;
      }
    }
    return slotKeys[0] ?? null;
  }, [
    regionCode,
    canPickScheduledSlot,
    requestedTimeSlot,
    requestedVisitAt,
    selectedSlotKey,
    slotKeys,
    visitDate,
  ]);

  const useScheduledSlot = canPickScheduledSlot && timeChoice === "slot";

  const activeTimeInputId =
    isScheduled && !useScheduledSlot ? CUSTOM_TIME_ID : VISIT_TIME_ID;
  const timeErrorId = fieldErrorId(activeTimeInputId);

  const rejectPastVisitAt = (
    dateStr: string,
    timeHHmm: string,
    target: FieldErrorTarget,
  ): boolean => {
    if (isFutureActivityLocalVisit(dateStr, timeHHmm, regionCode)) {
      return false;
    }
    setFieldError({
      message: t("handshakePages:confirmVisitMustBeFuture"),
      target,
    });
    return true;
  };

  const handleSubmit = () => {
    if (!visitDate) {
      setFieldError({
        message: t("handshakePages:confirmVisitRequired"),
        target: "visitDate",
      });
      return;
    }

    if (useScheduledSlot) {
      if (!effectiveSelectedSlotKey) {
        setFieldError({
          message: t("handshakePages:confirmSlotRequired"),
          target: "slot",
        });
        return;
      }
      if (rejectPastVisitAt(visitDate, effectiveSelectedSlotKey, "slot")) {
        return;
      }
      const visitAtIso = visitAtLocalPickerToApiIso(
        visitDate,
        effectiveSelectedSlotKey,
      );
      setFieldError(null);
      void onConfirm({
        visit_at: visitAtIso,
        time_slot: effectiveSelectedSlotKey,
        time_selection_mode: "SCHEDULED_SLOT",
      });
      return;
    }

    if (!customTime) {
      setFieldError({
        message: t("handshakePages:confirmVisitRequired"),
        target: "time",
      });
      return;
    }
    const hm = scheduleTimeToHHmm(customTime);
    if (rejectPastVisitAt(visitDate, hm, "time")) {
      return;
    }
    const visitAtIso = visitAtLocalPickerToApiIso(visitDate, hm);
    setFieldError(null);
    void onConfirm({
      visit_at: visitAtIso,
      time_slot: hm,
      time_selection_mode: "CUSTOM_TIME",
    });
  };

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen
      onClose={onClose}
      title={t("handshakePages:confirmBooking")}
      description={t("handshakePages:confirmBookingHint")}
      size={isScheduled ? "md" : "sm"}
      priority="high"
      showCloseButton={!isLoading}
      footer={
        <ModalFooter>
          <Button variant="primary" onClick={handleSubmit} isLoading={isLoading}>
            {t("handshakePages:confirmBooking")}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t("common:cancel")}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {requestedVisitAt ? (
          <Alert variant="info" title={t("handshakePages:bookingDetails.visitHeading")}>
            <p className="text-base font-bold text-surface-900">
              {regionCode
                ? formatActivityDateTime(
                    requestedVisitAt,
                    regionCode,
                    (time) => `(${time})`,
                  )?.dateLabel
                : null}
            </p>
            {requestedTimeLabel ? (
              <p className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-primary-800">
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                {requestedTimeLabel}
              </p>
            ) : null}
            <p className="mt-2 text-xs text-primary-700/80">
              {t("handshakePages:confirmVisitPrefillHint")}
            </p>
          </Alert>
        ) : null}

        <div>
          <Label htmlFor={VISIT_DATE_ID}>
            {t("handshakePages:confirmVisitDateLabel")}
          </Label>
          <input
            id={VISIT_DATE_ID}
            type="date"
            value={visitDate}
            min={todayActivityLocalDateInput(regionCode)}
            onChange={(e) => {
              setVisitDate(e.target.value);
              setFieldError(null);
            }}
            className={cn("mt-1", fieldControlClass({ size: "sm" }))}
            disabled={isLoading}
            aria-invalid={fieldError?.target === "visitDate" ? "true" : undefined}
            aria-describedby={
              fieldError?.target === "visitDate" ? visitDateErrorId : undefined
            }
          />
          {fieldError?.target === "visitDate" ? (
            <FieldError id={visitDateErrorId}>{fieldError.message}</FieldError>
          ) : null}
        </div>

        {isScheduled ? (
          <div className="space-y-3">
            <Label as="p" spacing="none">
              {t("handshakePages:confirmSlotsLabel")}
            </Label>
            {slotsLoading ? (
              <div className="flex items-center gap-2 text-sm text-surface-500">
                <Spinner size="sm" variant="muted"  loadingLabel={tCommon("loading")} />
                {t("handshakePages:confirmSlotsLoading")}
              </div>
            ) : null}
            {slotsLoadErrorMessage ? (
              <Alert variant="error" className="p-3">
                {slotsLoadErrorMessage}
              </Alert>
            ) : null}
            {!slotsLoading && !slotsError && dayClosed ? (
              <p className="text-sm text-surface-600">
                {t("handshakePages:confirmDayClosed")}
              </p>
            ) : null}
            {!slotsLoading && !slotsError && !dayClosed && slots.length === 0 ? (
              <EmptyState
                size="sm"
                showIcon={false}
                title={t("handshakePages:confirmNoSlots")}
                className="items-start px-0 py-0 text-left"
              />
            ) : null}
            {canPickScheduledSlot ? (
              <>
                <div
                  id={SLOTS_GROUP_ID}
                  className={cn(
                    "max-h-48 space-y-2 overflow-y-auto rounded-lg border p-2",
                    fieldError?.target === "slot"
                      ? "border-error-500"
                      : "border-surface-200",
                  )}
                  role="radiogroup"
                  aria-label={t("handshakePages:confirmSlotsLabel")}
                  aria-invalid={
                    fieldError?.target === "slot" ? "true" : undefined
                  }
                  aria-describedby={
                    fieldError?.target === "slot" ? slotsErrorId : undefined
                  }
                >
                {slots.map((slot) => {
                  const key = slotTimeKey(slot);
                  const selected = useScheduledSlot && effectiveSelectedSlotKey === key;
                  const isCustomerRequest = requestedSlotKey === key;
                  return (
                    <label
                      key={key}
                      className={`flex cursor-pointer items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                        selected
                          ? "border-primary-500 bg-primary-50 text-primary-900"
                          : isCustomerRequest
                            ? "border-primary-300 bg-primary-50/40 hover:border-primary-400"
                            : "border-surface-200 hover:border-surface-300"
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Radio
                          name="confirm-slot"
                          checked={selected}
                          onChange={() => {
                            setTimeChoice("slot");
                            setSelectedSlotKey(key);
                            setFieldError(null);
                          }}
                          disabled={isLoading}
                        />
                        <span className="font-medium tabular-nums">{key}</span>
                        {isCustomerRequest ? (
                          <Badge variant="primary" size="sm">
                            {t("handshakePages:confirmSlotRequested")}
                          </Badge>
                        ) : null}
                      </span>
                      <span
                        className={`shrink-0 text-xs ${slot.is_available ? "text-surface-600" : "text-warning-700"}`}
                      >
                        {formatSlotCapacity(slot, t, partySize)}
                      </span>
                    </label>
                  );
                })}
                </div>
                {fieldError?.target === "slot" ? (
                  <FieldError id={slotsErrorId}>{fieldError.message}</FieldError>
                ) : null}
              </>
            ) : null}

            <div
              className={`rounded-lg border p-3 ${
                !useScheduledSlot
                  ? "border-primary-500 bg-primary-50/50"
                  : "border-surface-200"
              }`}
            >
              <Label
                id={`${CUSTOM_TIME_ID}-mode`}
                spacing="none"
                className="flex cursor-pointer items-center gap-2"
              >
                <Radio
                  name="confirm-time-mode"
                  checked={!useScheduledSlot}
                  onChange={() => {
                    setTimeChoice("custom");
                    setFieldError(null);
                  }}
                  disabled={isLoading}
                />
                {t("handshakePages:confirmCustomTimeLabel")}
              </Label>
              {!useScheduledSlot ? (
                <>
                  <input
                    id={CUSTOM_TIME_ID}
                    type="time"
                    value={customTime}
                    onChange={(e) => {
                      setCustomTime(e.target.value);
                      setFieldError(null);
                    }}
                    className={cn("mt-2", fieldControlClass({ size: "sm" }))}
                    disabled={isLoading}
                    aria-labelledby={`${CUSTOM_TIME_ID}-mode`}
                    aria-invalid={
                      fieldError?.target === "time" ? "true" : undefined
                    }
                    aria-describedby={
                      fieldError?.target === "time" ? timeErrorId : undefined
                    }
                  />
                  {fieldError?.target === "time" ? (
                    <FieldError id={timeErrorId}>{fieldError.message}</FieldError>
                  ) : null}
                </>
              ) : null}
              <p className="mt-2 text-xs text-surface-500">
                {t("handshakePages:confirmCustomTimeHint")}
              </p>
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <Label htmlFor={VISIT_TIME_ID}>
              {t("handshakePages:confirmVisitTimeLabel")}
            </Label>
            <input
              id={VISIT_TIME_ID}
              type="time"
              value={customTime}
              onChange={(e) => {
                setCustomTime(e.target.value);
                setFieldError(null);
              }}
              className={cn("mt-1", fieldControlClass({ size: "sm" }))}
              disabled={isLoading}
              aria-invalid={fieldError?.target === "time" ? "true" : undefined}
              aria-describedby={
                fieldError?.target === "time" ? timeErrorId : undefined
              }
            />
            {fieldError?.target === "time" ? (
              <FieldError id={timeErrorId}>{fieldError.message}</FieldError>
            ) : null}
          </div>
        )}
      </div>
    </Modal>
  );
}
